import React from "react";
import { useQueryClient } from "@tanstack/react-query";

import type { Task } from "types/Task";
import { isUnreadTerminalLongTask } from "types/Task";
import usePatchTaskMutation from "services/dbApi/hooks/usePatchTaskMutation";
import { TASKS_QUERY_KEY } from "services/dbApi/hooks/useTasksQuery";

/**
 * Stamps `terminalSeenAt` on the unread terminal long-task rows in `threadId`,
 * which the user is now looking at: writes the tasks cache first, then PATCHes
 * each row (ADR 059, "Completion signalling").
 *
 * The cache-before-PATCH order is load-bearing, not a latency nicety. The one
 * caller is a render-driven effect keyed on the tasks cache, so without a local
 * write the row stays unread until the PATCH response echoes `terminalSeenAt`
 * back, the effect re-runs on that response, still selects the row, and PATCHes
 * again — without bound whenever the response differs from the cached row in any
 * field while omitting `terminalSeenAt`. Stamping the cache up front makes
 * `isUnreadTerminalLongTask` stop matching at once.
 *
 * Idempotent: `isUnreadTerminalLongTask` is false for a row that already carries
 * `terminalSeenAt`, so a repeat call selects nothing and is a no-op.
 */
export function useMarkTerminalSeen(): (
  tasks: Task[],
  threadId: string
) => void {
  const queryClient = useQueryClient();
  const { mutate: patchTask } = usePatchTaskMutation();

  return React.useCallback(
    (tasks, threadId) => {
      const unread = tasks.filter(
        (task) => task.threadId === threadId && isUnreadTerminalLongTask(task)
      );
      if (unread.length === 0) return;

      const seenAt = new Date().toISOString();
      const unreadIds = new Set(unread.map((task) => task.id));
      queryClient.setQueryData<Task[]>(TASKS_QUERY_KEY, (prev) =>
        prev?.map((task) =>
          unreadIds.has(task.id) ? { ...task, terminalSeenAt: seenAt } : task
        )
      );
      for (const task of unread) {
        patchTask({ id: task.id, patch: { terminalSeenAt: seenAt } });
      }
    },
    [queryClient, patchTask]
  );
}
